<template>
  <VaLayout
    :top="{ fixed: true, order: 1 }"
    :left="{ fixed: true, absolute: breakpoints.mdDown, order: 2, overlay: breakpoints.mdDown && !isSidebarMinimized }"
    @leftOverlayClick="isSidebarMinimized = true"
  >
    <template #top>
      <AppNavbar :is-mobile="isMobile" />
    </template>

    <template #left>
      <AppSidebar :minimized="isSidebarMinimized" :animated="!isMobile" :mobile="isMobile" />
    </template>

    <template #content>
      <div :class="{ minimized: isSidebarMinimized }" class="app-layout__sidebar-wrapper">
        <div v-if="isFullScreenSidebar" class="flex justify-end">
          <VaButton class="px-4 py-4" icon="md_close" preset="plain" @click="onCloseSidebarButtonClick" />
        </div>
      </div>
      <AppLayoutNavigation v-if="!isMobile" class="p-6" />
      <main class="app-layout__main p-6 pt-0 pb-0" style="z-index: 15">
        <RouterView />
      </main>
    </template>
  </VaLayout>
</template>

<script setup>
import { onBeforeUnmount, onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { onBeforeRouteUpdate } from 'vue-router'
import { useBreakpoint } from 'vuestic-ui'

import { useGlobalStore } from '../stores/global-store'
import { useUserStore } from '../stores/user-store'

import AppLayoutNavigation from '../components/app-layout-navigation/AppLayoutNavigation.vue'
import AppNavbar from '../components/navbar/AppNavbar.vue'
import AppSidebar from '../components/sidebar/AppSidebar.vue'

const GlobalStore = useGlobalStore()
const userStore = useUserStore()
userStore.fetchMe()

const breakpoints = useBreakpoint()

const sidebarWidth = ref('16rem')
const sidebarMinimizedWidth = ref(undefined)

const isMobile = ref(false)
const isTablet = ref(false)
const { isSidebarMinimized } = storeToRefs(GlobalStore)

const onResize = () => {
  isSidebarMinimized.value = breakpoints.mdDown
  isMobile.value = breakpoints.smDown
  isTablet.value = breakpoints.mdDown
  sidebarMinimizedWidth.value = isMobile.value ? '0' : '4.5rem'
  sidebarWidth.value = isTablet.value ? '100%' : '16rem'
}

onMounted(() => {
  window.addEventListener('resize', onResize)
  onResize()
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', onResize)
})

onBeforeRouteUpdate(() => {
  if (breakpoints.mdDown) {
    // Collapse sidebar after route change for Mobile
    isSidebarMinimized.value = true
  }
})

const isFullScreenSidebar = computed(() => isTablet.value && !isSidebarMinimized.value)

const onCloseSidebarButtonClick = () => {
  isSidebarMinimized.value = true
}
</script>

<style lang="scss" scoped>
// Prevent icon jump on animation
.va-sidebar {
  width: unset !important;
  min-width: unset !important;
}

// VaLayout's content row is "1fr", but grid only distributes leftover space
// into fr-tracks when the container has a *definite* height — min-height alone
// doesn't count, so it left a gap below short pages. Use height instead, and
// let the content area scroll internally for pages taller than the viewport.
// Not :deep() — VaLayout's root gets the scope attribute directly (single root
// forwarding), so :deep() (which expects it as an ancestor) never matches it.
.va-layout {
  height: 100vh;
}

// Make .main fill the rest of the content area's height (instead of shrinking
// to its content), with a single scrollbar living on .main itself — so pages
// using height: calc(100vh - Npx) for their own internal table scroll get the
// budget they expect, instead of a second, competing scroll container above them.
:deep(.va-layout__area--content) {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.app-layout__main {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
}

// Let <article> (RouterView's wrapper) stretch to fill .app-layout__main, so a
// page's VaCard can in turn fill it with height: 100% instead of a hardcoded
// calc(100vh - Npx) budget that breaks whenever the navbar/breadcrumb height changes.
article {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
</style>
